import i18n from "../../i18n";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Group,
  Modal,
  Select,
  Text,
  TextInput,
  NumberInput,
  Checkbox,
  Stack,
  Loader,
  Center,
  SegmentedControl,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import ArrayTable from "../arrayTable";
import SelectOptionsEditor from "./SelectOptionsEditor";
import {
  EXTRA_FIELD_TYPES,
  EXTRA_FIELD_PLACEMENT_OPTIONS,
  EXTRA_FIELD_COLUMN_OPTIONS,
  DEFAULT_EXTRA_FIELD_DISPLAY,
  normalizeExtraFieldDisplay,
} from "../../data/extraFieldTypes";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { FormSectionCard } from "../../layout/page";
import {
  fetchExtraFieldSetting,
  saveExtraFieldSetting,
} from "../../helpers/extraFieldSetting";

const apiEntity = "extraField";

const segmentLabelStyles = {
  label: {
    minWidth: 88,
    paddingLeft: 20,
    paddingRight: 20,
  },
};

const columnSegmentLabelStyles = {
  label: {
    minWidth: 56,
    paddingLeft: 20,
    paddingRight: 20,
  },
};

const rowInitValue = (entityType) => ({
  label: "",
  cname: "",
  fieldType: "TEXT",
  selectOptions: [],
  showInImport: false,
  showInEditForm: true,
  sortOrder: 0,
  active: true,
  entityType,
});

const ExtraFieldListEditor = ({ entityType }) => {
  const [api] = useServerApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const navigate = useNavigate();
  const tabNav = useTabNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [optionsModalIndex, setOptionsModalIndex] = useState(null);
  const [displayOptions, setDisplayOptions] = useState({
    ...DEFAULT_EXTRA_FIELD_DISPLAY,
  });
  const originalRowsRef = useRef([]);

  const form = useForm({
    initialValues: { fields: [] },
  });

  const loadFields = async ({ showLoading = false } = {}) => {
    if (showLoading) setLoading(true);
    try {
      const [result, setting] = await Promise.all([
        apiRef.current.search({
          apiEntity,
          pageSize: 200,
          currentPage: 1,
          sort: { by: "sortOrder", order: "asc" },
          searchQuery: { entityType },
          byAggregation: false,
        }),
        fetchExtraFieldSetting(apiRef.current, entityType),
      ]);
      const docs = (result?.docs ?? []).map((doc) => ({
        _id: doc._id,
        entityType: doc.entityType,
        key: doc.key,
        label: doc.label ?? "",
        cname: doc.cname ?? "",
        fieldType: doc.fieldType ?? "TEXT",
        selectOptions: doc.selectOptions ?? [],
        showInImport: !!doc.showInImport,
        showInEditForm: doc.showInEditForm !== false,
        sortOrder: doc.sortOrder ?? 0,
        active: doc.active !== false,
      }));
      originalRowsRef.current = docs.map((d) => ({ ...d }));
      form.setValues({ fields: docs });
      setDisplayOptions(normalizeExtraFieldDisplay(setting));
    } catch (error) {
      showNotification({
        color: "red",
        title: "Load failed",
        message: apiRef.current.getErrorMsg(error),
      });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    loadFields({ showLoading: true });
    // api from useServerApi is a new object each render — only reload when tab changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType]);

  const tableFields = [
    {
      header: "Label",
      headerWidth: "18%",
      fieldName: "label",
      component: TextInput,
      props: { size: "xs", placeholder: "English label" },
    },
    {
      header: "Label (Chinese)",
      headerWidth: "16%",
      fieldName: "cname",
      component: TextInput,
      props: { size: "xs", placeholder: "中文標籤" },
    },
    {
      header: "Type",
      headerWidth: "18%",
      fieldName: "fieldType",
      component: Select,
      props: {
        size: "xs",
        data: EXTRA_FIELD_TYPES,
        searchable: true,
      },
    },
    {
      header: "Sort",
      headerWidth: "8%",
      fieldName: "sortOrder",
      component: NumberInput,
      props: { size: "xs", min: 0 },
    },
    {
      header: "Import",
      headerWidth: "7%",
      fieldName: "showInImport",
      type: "checkbox",
      component: Checkbox,
      props: { size: "xs" },
    },
    {
      header: "Edit Form",
      headerWidth: "9%",
      fieldName: "showInEditForm",
      type: "checkbox",
      component: Checkbox,
      props: { size: "xs" },
    },
    {
      header: "Active",
      headerWidth: "7%",
      fieldName: "active",
      type: "checkbox",
      component: Checkbox,
      props: { size: "xs" },
    },
  ];

  const onDataChange = (rows) => {
    form.setFieldValue("fields", rows ?? []);
  };

  const validateFields = (rows) => {
    const validRows = rows.filter((row) => String(row.label ?? "").trim());
    for (const row of validRows) {
      if (
        row.fieldType === "SELECT" &&
        (!row.selectOptions || row.selectOptions.length === 0)
      ) {
        showNotification({
          color: "red",
          title: "Validation",
          message: `Select field "${row.label}" needs at least one option.`,
        });
        return null;
      }
    }
    return validRows;
  };

  const handleClose = () => {
    if (tabNav.enabled) {
      tabNav.closeActive();
      return;
    }
    navigate("/management/setting");
  };

  const handleSave = async () => {
    const rows = form.values.fields ?? [];
    const validRows = validateFields(rows);
    if (!validRows) return;

    const currentIds = new Set(
      validRows.filter((row) => row._id).map((row) => String(row._id))
    );

    try {
      setSaving(true);

      await saveExtraFieldSetting(apiRef.current, entityType, displayOptions);

      for (const original of originalRowsRef.current) {
        if (original._id && !currentIds.has(String(original._id))) {
          await apiRef.current.removeById({ apiEntity, id: original._id });
        }
      }

      for (const row of validRows) {
        const payload = {
          entityType,
          label: row.label.trim(),
          cname: row.cname?.trim() ?? "",
          fieldType: row.fieldType,
          selectOptions:
            row.fieldType === "SELECT" ? row.selectOptions ?? [] : [],
          showInImport: !!row.showInImport,
          showInEditForm: row.showInEditForm !== false,
          sortOrder: Number(row.sortOrder) || 0,
          active: row.active !== false,
        };

        if (row._id) {
          const result = await apiRef.current.updateById({
            apiEntity,
            id: row._id,
            values: payload,
          });
          if (result && result.success === false) {
            throw result.error || new Error(result.message || "Update failed");
          }
        } else {
          const result = await apiRef.current.add({ apiEntity, values: payload });
          if (result && result.success === false) {
            throw result.error || new Error(result.message || "Add failed");
          }
        }
      }

      showNotification({
        title: "Saved",
        message: "Extra fields updated.",
      });
      await loadFields();
    } catch (error) {
      showNotification({
        color: "red",
        title: "Save failed",
        message:
          error?.message ||
          apiRef.current.getErrorMsg(error) ||
          "Unable to save extra fields.",
      });
    } finally {
      setSaving(false);
    }
  };

  const optionsRow =
    optionsModalIndex != null
      ? form.values.fields?.[optionsModalIndex]
      : null;

  const selectRows = (form.values.fields ?? [])
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.fieldType === "SELECT");

  return (
    <>
      <Group position="right" mb="md">
        <Button
          variant="default"
          size="xs"
          onClick={handleClose}
          disabled={saving}
        >{i18n.t("Close")}</Button>
        <Button size="xs" onClick={handleSave} loading={saving}>{i18n.t("Save")}</Button>
      </Group>

      <FormSectionCard title="Display options" mb="md">
        {loading ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : (
          <Group spacing="xl" align="flex-end">
            <Stack spacing={4}>
              <Text size="xs" color="dimmed">
                Display Tab
              </Text>
              <SegmentedControl
                size="xs"
                value={displayOptions.placement}
                onChange={(value) =>
                  setDisplayOptions((current) => ({
                    ...current,
                    placement: value,
                  }))
                }
                data={EXTRA_FIELD_PLACEMENT_OPTIONS}
                styles={segmentLabelStyles}
              />
            </Stack>
            <Stack spacing={4}>
              <Text size="xs" color="dimmed">
                Field Columns
              </Text>
              <SegmentedControl
                size="xs"
                value={String(displayOptions.columns)}
                onChange={(value) =>
                  setDisplayOptions((current) => ({
                    ...current,
                    columns: Number(value),
                  }))
                }
                data={EXTRA_FIELD_COLUMN_OPTIONS.map((option) => ({
                  label: option.label,
                  value: String(option.value),
                }))}
                styles={columnSegmentLabelStyles}
              />
            </Stack>
          </Group>
        )}
      </FormSectionCard>

      <FormSectionCard
        title={`${entityType} custom fields`}
        mb="md"
      >
        {loading && (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        )}

        {!loading && (
          <>
            <ArrayTable
              form={form}
              name="fields"
              rowInitValue={rowInitValue(entityType)}
              fields={tableFields}
              onDataChange={onDataChange}
              readonly={false}
            />

            {selectRows.length > 0 && (
              <Stack spacing={4} mt="sm">
                <Text size="xs" color="dimmed">{i18n.t("Select list options")}</Text>
                {selectRows.map(({ row, index }) => (
                  <Group key={`select-${index}`} spacing="xs">
                    <Text size="xs">
                      Row {index + 1}
                      {row.label ? ` — ${row.label}` : ""}:
                    </Text>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => setOptionsModalIndex(index)}
                    >
                      {(row.selectOptions?.length ?? 0)
                        ? `${row.selectOptions.length} option(s)`
                        : "Configure options"}
                    </Button>
                  </Group>
                ))}
              </Stack>
            )}

          </>
        )}
      </FormSectionCard>

      <Modal
        opened={optionsModalIndex != null}
        onClose={() => setOptionsModalIndex(null)}
        title="Select list options"
        size="md"
      >
        {optionsRow && (
          <SelectOptionsEditor
            value={optionsRow.selectOptions ?? []}
            onChange={(next) => {
              const rows = [...(form.values.fields ?? [])];
              rows[optionsModalIndex] = {
                ...rows[optionsModalIndex],
                selectOptions: next,
              };
              form.setFieldValue("fields", rows);
            }}
          />
        )}
      </Modal>
    </>
  );
};

export default ExtraFieldListEditor;
